import styled from 'styled-components';

import { Button } from '@/components/ui/button';

import { BundleState } from 'data/__generated__';

import { TrelloView } from 'components/TrelloView/TrelloView';

import { BundleTrelloItem } from './components/BundleTrelloItem';
import { getColorsFromState } from './components/StatusTag';
import { BUNDLE_PLURAL_STATES_LABELS } from './constants/states';
import type { BundleRow } from './constants/bundlesColumns';

type BundlesTrelloViewProps = {
    bundles: BundleRow[];
    selectedBundleIds: string[];
    setSelectedBundleIds: React.Dispatch<React.SetStateAction<string[]>>;
    toggleBundleSelection: (bundleId: string) => void;
    /** Le kanban n'a pas de défilement unique où accrocher un chargement continu : il le demande. */
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
};

/** L'ordre des colonnes suit le cycle de préparation, les états terminaux à la fin. */
const STATE_COLUMNS_TO_DISPLAY: BundleState[] = [
    BundleState.Created,
    BundleState.Started,
    BundleState.InProgress,
    BundleState.Paused,
    BundleState.Completed,
    BundleState.Aborted,
    BundleState.Archived,
];

/** Dans une colonne terminale, c'est le plus récent qui intéresse : l'ordre s'y inverse. */
const REVERSED_STATES: BundleState[] = [BundleState.Completed, BundleState.Aborted, BundleState.Archived];

// La préparation la plus proche en premier ; à date égale, l'ordre de création (le numéro).
function sortByPreparationDate(bundleA: BundleRow, bundleB: BundleRow): number {
    if (bundleA.scheduledPreparationDate !== bundleB.scheduledPreparationDate) {
        return bundleA.scheduledPreparationDate < bundleB.scheduledPreparationDate ? -1 : 1;
    }
    return bundleA.number - bundleB.number;
}

export const BundlesTrelloView = ({
    bundles,
    selectedBundleIds,
    setSelectedBundleIds,
    toggleBundleSelection,
    hasMore,
    isLoadingMore,
    onLoadMore,
}: BundlesTrelloViewProps) => {
    function getBundlesByState(state: BundleState) {
        const sortedBundles = bundles.filter((bundle) => bundle.state === state).sort(sortByPreparationDate);
        if (REVERSED_STATES.includes(state)) {
            sortedBundles.reverse();
        }
        return sortedBundles.map((bundle) => ({
            _id: bundle._id,
            content: (
                <BundleTrelloItem
                    bundle={bundle}
                    isSelected={selectedBundleIds.includes(bundle._id)}
                    toggleBundleSelection={toggleBundleSelection}
                />
            ),
        }));
    }

    const initialData = {
        columns: STATE_COLUMNS_TO_DISPLAY.map((state) => ({
            title: BUNDLE_PLURAL_STATES_LABELS[state],
            accentColor: getColorsFromState(state).backgroundColor,
            items: getBundlesByState(state),
        })),
    };

    return (
        <TrelloViewContainer>
            {hasMore ? (
                // Le compte des colonnes ne porte que sur ce qui est chargé : le dire, plutôt que de
                // laisser croire qu'une colonne courte est une colonne vide.
                <LoadMoreBanner>
                    <span>
                        {bundles.length} bundles chargés — il en reste au-delà, les colonnes ne les comptent pas encore.
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
                        {isLoadingMore ? 'Chargement…' : 'Charger la suite'}
                    </Button>
                </LoadMoreBanner>
            ) : null}
            <TrelloScrollArea>
                <TrelloView
                    initialData={initialData}
                    selectedItemIds={selectedBundleIds}
                    setSelectedItemIds={setSelectedBundleIds}
                    columnWidth="250px"
                    padding="0"
                />
            </TrelloScrollArea>
        </TrelloViewContainer>
    );
};

/**
 * Colonne flex : la bannière prend sa hauteur propre, le kanban le reste. Sans ça, `TrelloView` fait
 * `h-full` de CE conteneur et s'ajoute à la bannière — d'où une barre de défilement verticale.
 */
const TrelloViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0;
`;

/** Le défilement horizontal vit ici. Jamais de vertical : chaque colonne a déjà le sien. */
const TrelloScrollArea = styled.div`
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
`;

const LoadMoreBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 8px 12px;
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;
